import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import { getAPIPrefixForRT } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import { getRelevantScrapingConfigIDsForSelectedDevice } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportUtils";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery, useRTApiDuration } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import type { AvailableSERPFeatures } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { apiFormat } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { retryOnAbort } from "app/UtilitiesAndConstants/UtilityFunctions/query";

export const useKeywordsSerpFeaturesQuery = () => {
    const { currentCampaignConfiguration } = useKeywordsCampaignsContext();
    const { params } = useCurrentStateAndParams<RTRawParams>();
    const { campaignId, device, locationsIds } = params;
    const durationObject = useRTApiDuration();

    const apiParams: Record<string, unknown> | null = useMemo(() => {
        const selectedSite = currentCampaignConfiguration?.site;

        if (!currentCampaignConfiguration || !device || !durationObject || !locationsIds || !selectedSite) {
            return null;
        }
        const relevantScrapingConfigs = getRelevantScrapingConfigIDsForSelectedDevice(
            device,
            currentCampaignConfiguration.scrapingConfigurations,
            locationsIds,
        );
        const from = durationObject?.from?.format(apiFormat);
        const to = durationObject?.to?.format(apiFormat);
        return {
            sites: selectedSite,
            campaignguid: currentCampaignConfiguration.id,
            from,
            to,
            scrapingConfigurationGuids: relevantScrapingConfigs,
            isDaily: true,
        };
    }, [currentCampaignConfiguration, device, durationObject, locationsIds]);

    return useRankTrackerQuery<AvailableSERPFeatures | undefined>({
        queryKey: createCampaignQueryKey(currentCampaignConfiguration?.id, RankTrackerQueryType.KeywordsSerpFeatures, [
            apiParams,
        ]),
        enabled: !!currentCampaignConfiguration && currentCampaignConfiguration.id === campaignId && !!apiParams,
        queryFn: async ({ signal: cancellation }) => {
            if (!apiParams) {
                return undefined;
            }
            const apiPrefix = getAPIPrefixForRT(!!currentCampaignConfiguration.isDemo);
            const endpoint = `${apiPrefix}/settings/SerpFilter`;
            return DefaultFetchService.getInstance().get<AvailableSERPFeatures>(endpoint, apiParams, {
                cancellation,
            });
        },
        retry: retryOnAbort,
        throwOnError: (error) => {
            Sentry.errorTracker({
                message: "Error fetching available SERP features for keywords",
                e: error,
                level: "error",
            });
            return false;
        },
    });
};
